import React, { memo, useCallback, useEffect, useMemo } from "react";
import {
  Center,
  ChakraProvider,
  Flex,
  VStack,
  Text,
  Button,
} from "@chakra-ui/react";
import useQueryString from "modules/hooks/use-query-string";
import colors from "app/colors";
import { chakraTheme } from "modules/providers/chakra-provider/chakra-theme";
import { LogoIcon, LogoTextIcon } from "modules/icons";
import { useRouter } from "next/router";
import { AUTH_REDIRECT_PATH } from "modules/utils/Constants";
import { getJwtTokenSub, isTokenExpired } from "modules/utils/Functions";
import { NEXT_PUBLIC_API_BASE_URL } from "app/client";

const API_UNSUBSCRIBE_PATH = "/unsubscribe/token";

const UnsubscribePage = () => {
  const { qsToken } = useQueryString();
  const { push } = useRouter();

  const email = useMemo(() => getJwtTokenSub(qsToken), [qsToken]);

  const _isTokenExpired = useMemo(() => isTokenExpired(qsToken), [qsToken]);

  const onGoToInboxClicked = useCallback(() => {
    push({
      pathname: AUTH_REDIRECT_PATH,
    });
  }, [push]);

  const unsubscribeAPI = useCallback(
    () =>
      fetch(`${NEXT_PUBLIC_API_BASE_URL}${API_UNSUBSCRIBE_PATH}`, {
        method: "POST",
        body: JSON.stringify({
          token: qsToken,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    [qsToken],
  );

  useEffect(() => {
    unsubscribeAPI();
  }, [unsubscribeAPI]);

  return (
    <ChakraProvider theme={chakraTheme}>
      <Flex h="100dvh" w="full" bg={colors.darkBlue} p={0} m={0}>
        <Center w="full" h="full">
          <VStack
            w={444}
            bg={colors.white}
            px={10}
            py={8}
            borderRadius={32}
            spacing={3}
          >
            <LogoIcon boxSize={20} />
            <LogoTextIcon fill={colors.darkBlue} />
            {_isTokenExpired ? (
              <Text fontFamily="Outfit" fontSize={22} fontWeight={700}>
                This unsubscribe link has expired. Please use an unsubscribe
                link from a more recent email.
              </Text>
            ) : (
              <>
                <Text fontFamily="Outfit" fontSize={22} fontWeight={700}>
                  You are now Unsubscribed
                </Text>
                <Text color={colors.grayMain} fontSize={16} fontWeight={500}>
                  <Text as="span" fontWeight={700}>
                    {email}
                  </Text>{" "}
                  will no longer receive notifications about unread messages in
                  Synco.
                </Text>
              </>
            )}
            <Button w="full" onClick={onGoToInboxClicked}>
              Go to Inbox
            </Button>
          </VStack>
        </Center>
      </Flex>
    </ChakraProvider>
  );
};

UnsubscribePage.prefersFullScreenLayout = true;

export default memo(UnsubscribePage);
